import { useEffect, useRef, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2, AlertCircle, Check } from "lucide-react";

// ---------------------------------------------------------------------------
// AddressAutocomplete — Powered by OpenStreetMap (Photon by Komoot).
// No API key. No signup. No credit card. Works instantly.
// Free public endpoint with no rate limit for normal app usage.
// Biased to the Columbus, OH metro area for relevance.
// ---------------------------------------------------------------------------

// Photon endpoint — public, free, OSM-based, fast, no key required.
const PHOTON_URL = "https://photon.komoot.io/api/";
// Bias query results around Columbus, OH (lat,lon).
const COLUMBUS = { lat: 39.9612, lon: -82.9988 };

/** Map a Photon feature into the four fields we need. */
function parseFeature(feature) {
  const p = feature?.properties || {};
  // Photon lumps "house number + street" separately. Build a clean street.
  const street = [p.housenumber, p.street].filter(Boolean).join(" ").trim() || p.name || "";
  return {
    street,
    city: p.city || p.town || p.village || p.county || "",
    state: stateAbbr(p.state) || p.state || "",
    zip: p.postcode || "",
    label: [
      [p.housenumber, p.street].filter(Boolean).join(" "),
      p.city || p.town || p.village,
      p.state,
      p.postcode,
    ].filter(Boolean).join(", "),
  };
}

// Photon returns full state names; convert to USPS 2-letter codes for AcroForm fields.
const STATES = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO",
  montana: "MT", nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC", "north dakota": "ND",
  ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI",
  "south carolina": "SC", "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  vermont: "VT", virginia: "VA", washington: "WA", "west virginia": "WV",
  wisconsin: "WI", wyoming: "WY",
};
function stateAbbr(name) { return name ? STATES[name.toLowerCase()] : ""; }

/** Debounce helper — invoke `fn` after `wait` ms of inactivity. */
function useDebouncedCallback(fn, wait) {
  const timer = useRef(null);
  return useCallback((...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), wait);
  }, [fn, wait]);
}

export default function AddressAutocomplete({
  value, onStreet, onCity, onState, onZip,
  required = false, label = "Home address", testId = "address-autocomplete",
}) {
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const [zipBusy, setZipBusy] = useState(false);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    const q = (query || "").trim();
    if (q.length < 3) { setSuggestions([]); return; }
    setBusy(true); setError("");
    try {
      const url = `${PHOTON_URL}?q=${encodeURIComponent(q)}&lat=${COLUMBUS.lat}&lon=${COLUMBUS.lon}&limit=6&osm_tag=place,highway,building&lang=en`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      // Keep only US results — Photon's bias is soft, not a hard filter.
      const feats = (j.features || []).filter((f) => {
        const cc = (f.properties?.countrycode || "").toLowerCase();
        return cc === "us" || !cc; // some entries lack countrycode
      });
      setSuggestions(feats.map(parseFeature));
      setOpen(true);
      setActiveIdx(-1);
    } catch (err) {
      setError("Address suggestions are temporarily unavailable. You can type the address manually — ZIP autofill still works.");
      setSuggestions([]);
    } finally { setBusy(false); }
  }, []);

  const debouncedFetch = useDebouncedCallback(fetchSuggestions, 250);

  const onChange = (raw) => {
    onStreet?.(raw);
    debouncedFetch(raw);
  };

  const pick = (s) => {
    if (s.street) onStreet?.(s.street);
    if (s.city && onCity) onCity(s.city);
    if (s.state && onState) onState(s.state);
    if (s.zip && onZip) onZip(s.zip);
    setOpen(false);
    setSuggestions([]);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0) {
        e.preventDefault();
        pick(suggestions[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  /** Free fallback — when the user types/pastes a 5-digit ZIP, we look it up
   * via the public zippopotam.us API and pre-fill city + state. */
  const tryZipFromValue = async (text) => {
    const m = (text || "").match(/\b(\d{5})\b/);
    if (!m) return;
    setZipBusy(true);
    try {
      const r = await fetch(`https://api.zippopotam.us/us/${m[1]}`);
      if (!r.ok) return;
      const j = await r.json();
      const place = j.places?.[0];
      if (!place) return;
      if (place["place name"] && onCity) onCity(place["place name"]);
      if (place["state abbreviation"] && onState) onState(place["state abbreviation"]);
      if (onZip) onZip(m[1]);
    } catch { /* offline / blocked */ }
    finally { setZipBusy(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5 text-emerald-700" />
        {label}{required && <span className="text-red-600"> *</span>}
        {(busy || zipBusy) && <Loader2 className="w-3 h-3 animate-spin text-stone-400" />}
      </Label>
      <Input
        ref={inputRef}
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => tryZipFromValue(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        placeholder="Start typing… e.g. 1234 Brown Rd, Columbus"
        autoComplete="off"
        required={required}
        className="mt-1.5 h-12 text-base"
      />
      {open && suggestions.length > 0 && (
        <div
          data-testid={`${testId}-dropdown`}
          className="absolute z-30 mt-1 w-full bg-white border border-stone-200 rounded-xl shadow-xl overflow-hidden"
        >
          {suggestions.map((s, i) => (
            <button
              key={`${s.label}-${i}`}
              type="button"
              data-testid={`${testId}-suggestion-${i}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-start gap-2 transition-colors ${i === activeIdx ? "bg-emerald-50 text-emerald-900" : "hover:bg-stone-50"}`}
            >
              {i === activeIdx ? <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-700" /> : <MapPin className="w-3.5 h-3.5 mt-0.5 text-stone-400" />}
              <span className="truncate">{s.label}</span>
            </button>
          ))}
        </div>
      )}
      <p className="mt-1 text-xs text-stone-500">
        {error ? (
          <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle className="w-3 h-3" /> {error}</span>
        ) : (
          "Free address search powered by OpenStreetMap. Type 3+ letters for live suggestions, or paste a 5-digit ZIP and tab out."
        )}
      </p>
    </div>
  );
}
